import { type FeatureFlagsState, FeatureFlag } from './flagsTypes';

const defaultState: Readonly<FeatureFlagsState> = {
  depthPeelingLayers: 0,
  disableTransparencyAngleBasedCompensation: false,
  disableTransparencyFlatCompensation: false,
  parallax: false,
  premultipliedAlphaLogos: true,
  wireframeFabrics: false,
} as const;

const globalState: FeatureFlagsState = {
  depthPeelingLayers: defaultState.depthPeelingLayers,
  disableTransparencyAngleBasedCompensation: defaultState.disableTransparencyAngleBasedCompensation,
  disableTransparencyFlatCompensation: defaultState.disableTransparencyFlatCompensation,
  parallax: defaultState.parallax,
  premultipliedAlphaLogos: defaultState.premultipliedAlphaLogos,
  wireframeFabrics: defaultState.wireframeFabrics,
};

/**
 * Initialize flags state.
 * @param state - New state.
 */
export const init = (state?: Partial<FeatureFlagsState>) => {
  // Complete state with default values
  state ??= {};
  state.depthPeelingLayers ??= defaultState.depthPeelingLayers;
  state.disableTransparencyAngleBasedCompensation ??=
    defaultState.disableTransparencyAngleBasedCompensation;
  state.disableTransparencyFlatCompensation ??= defaultState.disableTransparencyFlatCompensation;
  state.parallax ??= defaultState.parallax;
  state.premultipliedAlphaLogos ??= defaultState.premultipliedAlphaLogos;
  state.wireframeFabrics ??= defaultState.wireframeFabrics;
  // Assign initialization values
  globalState.depthPeelingLayers = state.depthPeelingLayers;
  globalState.disableTransparencyAngleBasedCompensation =
    state.disableTransparencyAngleBasedCompensation;
  globalState.disableTransparencyFlatCompensation = state.disableTransparencyFlatCompensation;
  globalState.parallax = state.parallax;
  globalState.premultipliedAlphaLogos = state.premultipliedAlphaLogos;
  globalState.wireframeFabrics = state.wireframeFabrics;
};

/**
 * Query for flags status.
 * @param key - Flags key.
 */
export const isEnabled = (key: FeatureFlag): boolean => {
  switch (key) {
    case FeatureFlag.DEPTH_PEELING:
      return globalState.depthPeelingLayers > 0;
    case FeatureFlag.DISABLE_TRANSPARENCY_ANGLE_BASED_COMPENSATION:
      return globalState.disableTransparencyAngleBasedCompensation;
    case FeatureFlag.DISABLE_TRANSPARENCY_FLAT_COMPENSATION:
      return globalState.disableTransparencyFlatCompensation;
    case FeatureFlag.PARALLAX:
      return globalState.parallax;
    case FeatureFlag.PREMULTIPLIED_ALPHA_LOGOS:
      return Boolean(globalState.premultipliedAlphaLogos);
    case FeatureFlag.WIREFRAME_FABRICS:
      return globalState.wireframeFabrics;
    default:
      return false;
  }
};

/**
 * Get flags state.
 */
export const getState = (): Readonly<FeatureFlagsState> => {
  return globalState;
};

/**
 * Get flag value.
 * @param flag - Feature.
 */
export const getValue = (flag: FeatureFlag): any => {
  switch (flag) {
    case FeatureFlag.DEPTH_PEELING:
      return globalState.depthPeelingLayers;
    default:
      return false;
  }
};
